// config/security.js
// PrizeReal — Constantes y configuración del sistema de seguridad (10 capas)
// Este archivo es la fuente de verdad para todos los parámetros de seguridad.
// Cambiar un valor acá lo propaga a todos los hooks y pantallas.

// ─── CAPA 1 — Identidad ───────────────────────────────────────────────────────
export const IDENTITY = {
  // Número de días que debe tener una cuenta para considerarse "establecida"
  NEW_ACCOUNT_DAYS: 7,

  // Una cuenta nueva pasa a revisión manual en canjes (flag en Firestore)
  NEW_ACCOUNT_REQUIRES_REVIEW: true,
};

// ─── CAPA 2 — Validación de clicks ───────────────────────────────────────────
export const CLICK_VALIDATION = {
  // Segundos mínimos que el contacto debe estar en la landing para contar el click
  MIN_TIME_ON_PAGE_SECONDS: 3,

  // Máximo de clicks válidos por IP por link (deduplicación)
  MAX_CLICKS_PER_IP_PER_LINK: 1,

  // Campo en Firestore donde se almacenan los clicks
  FIRESTORE_COLLECTION: "clicks",
};

// ─── CAPA 3 — QR con expiración criptográfica ────────────────────────────────
export const QR_SECURITY = {
  // Duración del token QR en segundos (captura de pantalla inútil después de esto)
  TOKEN_EXPIRY_SECONDS: 60,

  // Longitud del token aleatorio en bytes (hex → 32 chars)
  TOKEN_BYTES: 16,

  // Algoritmo de firma (Web Crypto API)
  HASH_ALGORITHM: "SHA-256",

  // Prefijo para identificar tokens PrizeReal en el QR
  TOKEN_PREFIX: "prz_",
};

// ─── CAPA 4 — Geofencing ─────────────────────────────────────────────────────
export const GEOFENCING = {
  // Radio máximo permitido en metros para el canje presencial
  MAX_DISTANCE_METERS: 300,

  // Jerarquía de precisión GPS (metros de error esperado)
  GPS_ACCURACY: {
    GPS:  { min: 3,   max: 10  },
    WIFI: { min: 15,  max: 40  },
    CELL: { min: 100, max: 300 },
  },

  // Si no hay GPS disponible, el QR solo es suficiente pero se registra la ausencia
  ALLOW_QR_WITHOUT_GPS: true,

  // Flag en Firestore cuando se canjea sin GPS confirmado
  GPS_ABSENT_FLAG: "gps_absent",
};

// ─── CAPA 5 — Detección de mock location ─────────────────────────────────────
export const MOCK_LOCATION = {
  // Si se detecta mock location, se requiere paso adicional antes de canjear
  REQUIRE_EXTRA_VERIFICATION: true,

  // Flag guardado en el registro de canje cuando se detecta mock location
  FLAG_KEY: "mock_location_detected",
};

// ─── CAPA 6 — Rate limiting en canjes ────────────────────────────────────────
export const RATE_LIMITING = {
  // Días de cooldown entre canjes del mismo usuario en el mismo comercio
  REDEMPTION_COOLDOWN_DAYS: 30,

  // Máximo de canjes por usuario por día (a definir con datos reales; provisional)
  MAX_REDEMPTIONS_PER_DAY: 5,

  // Máximo de canjes por comercio por día (protección anti-abuse desde el lado del negocio)
  MAX_REDEMPTIONS_PER_BUSINESS_PER_DAY: 200,
};

// ─── CAPA 7 — Snapshot de oferta ─────────────────────────────────────────────
export const OFFER_SNAPSHOT = {
  // Campos de la oferta que se guardan en el momento de compartir
  // Garantiza que el usuario cobra lo prometido aunque el comercio cambie la oferta
  FIELDS_TO_SNAPSHOT: ["prize_user", "prize_friends", "prize_clicks_needed"],

  // Campo en Firestore dentro del documento de click/share
  SNAPSHOT_KEY: "offer_snapshot",
};

// ─── CAPA 8 — Trust score ─────────────────────────────────────────────────────
export const TRUST_SCORE = {
  // Score inicial de todo usuario nuevo
  INITIAL_SCORE: 50,

  // Score mínimo para que los clicks cuenten sin verificación adicional
  MIN_SCORE_FOR_FULL_WEIGHT: 30,

  // Deltas por evento
  DELTA: {
    LEGITIMATE_REDEMPTION:     +5,   // Canje verificado exitoso
    SUSPECTED_SELF_CLICK:     -10,   // Click desde IP propia o VPN conocida
    MOCK_LOCATION_DETECTED:   -15,   // GPS falso detectado
    DISPUTE_RESOLVED_AGAINST:  -20,  // Disputa resuelta en contra del usuario
    ACCOUNT_FLAGGED:          -25,   // Cuenta marcada manualmente
    RATING_GIVEN:              +1,   // Calificó al comercio después del canje
  },

  // Score por debajo del cual los clicks pesan menos (multiplicador 0.5)
  LOW_TRUST_THRESHOLD: 20,
};

// ─── CAPA 9 — Anti-collusion comercio-usuario ────────────────────────────────
export const ANTI_COLLUSION = {
  // % máximo de canjes de cuentas < NEW_ACCOUNT_DAYS antes de disparo de revisión
  NEW_ACCOUNT_REDEMPTION_THRESHOLD_PCT: 80,

  // Un usuario/comercio baneado en una ciudad no puede registrarse en otra
  GLOBAL_BLACKLIST_ENABLED: true,
};

// ─── CAPA 10 — Reputación bilateral ──────────────────────────────────────────
export const REPUTATION = {
  // Escala de calificación
  RATING_MIN: 1,
  RATING_MAX: 5,

  // Promedio por debajo del cual el comercio pierde el badge Featured
  BUSINESS_FEATURED_MIN_RATING: 3.5,

  // Mínimo de calificaciones para aplicar el umbral (evita penalizar con pocos datos)
  BUSINESS_FEATURED_MIN_RATINGS_COUNT: 10,

  // SLA de resolución de disputas en horas
  DISPUTE_SLA_HOURS: 48,
};

// ─── Sistema de niveles (usuarios) ───────────────────────────────────────────
// Coincide exactamente con lo definido en el pitch y checkpoint
export const USER_LEVELS = [
  {
    level: 1,
    name:  "Buzzer",
    emoji: "⚡",
    minClicks: 0,
    maxClicks: 20,
    prizeMultiplier: 1.0,
    perks: ["standard_prizes"],
  },
  {
    level: 2,
    name:  "Hot Buzzer",
    emoji: "🔥",
    minClicks: 21,
    maxClicks: 100,
    prizeMultiplier: 1.1,
    perks: ["plus_10_percent"],
  },
  {
    level: 3,
    name:  "Diamond Rep",
    emoji: "💎",
    minClicks: 101,
    maxClicks: 500,
    prizeMultiplier: 1.0, // los perks son cualitativos, no multiplicativos
    perks: ["vip_offers", "early_access"],
  },
  {
    level: 4,
    name:  "City Legend",
    emoji: "👑",
    minClicks: 501,
    maxClicks: Infinity,
    prizeMultiplier: 1.0,
    perks: ["top_leaderboard", "exclusive_prizes"],
  },
];

/**
 * Dado el total de clicks acumulados, retorna el nivel del usuario.
 * @param {number} totalClicks
 * @returns {object} nivel correspondiente de USER_LEVELS
 */
export const getUserLevel = (totalClicks) =>
  USER_LEVELS.find(
    (l) => totalClicks >= l.minClicks && totalClicks <= l.maxClicks
  ) ?? USER_LEVELS[0];
